/**
 * Mattress upsell drawer.
 *
 * Selection: choosing a mattress variant stores it in memory and updates
 * the trigger row — no cart add at this point.
 *
 * Cart add: we patch window.fetch to intercept the APO's /cart/add.js
 * call and inject the mattress as a nested child item in the SAME request
 * using Shopify's nested cart lines API:
 *   { id: mattressVariantId, quantity: 1, parent_id: bedVariantId }
 *
 * This is required because parent_id only works when both items are sent
 * together — you cannot reference an existing cart line after the fact.
 */

(function () {
  // The fetch patch lives in layout/theme.liquid as an early synchronous
  // script so it runs before the APO app captures window.fetch at init time.
  // This file only needs to set/clear window.__mattressVariantId.

  // The trigger's default label is rendered server-side from the section
  // setting; capture it once so a reset restores that copy rather than a
  // hardcoded string that drifts from the merchant's configured value.
  const defaultMattressLabel =
    document.getElementById('MattressValue')?.textContent?.trim() ?? '';

  function clearMattressLabel() {
    const valueEl = document.getElementById('MattressValue');
    if (valueEl) valueEl.textContent = defaultMattressLabel;
  }

  /* ── Filter variant buttons by selected bed size ───────────────────── */
  function getSelectedBedSize() {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return null;
    // Size is always the first fieldset (option index 0)
    const sizeFieldset = variantSelects.querySelectorAll('fieldset')[0];
    if (!sizeFieldset) return null;
    const checked = sizeFieldset.querySelector('input[type="radio"]:checked');
    return checked ? checked.value : null;
  }

  function filterMattressVariantsBySize() {
    const bedSize = getSelectedBedSize();
    if (!bedSize) return;

    const needle = bedSize.toLowerCase();

    document.querySelectorAll('.mattress-card').forEach((card) => {
      let hasMatch = false;
      card.querySelectorAll('.mattress-card__variant-btn').forEach((btn) => {
        // Match if variant title starts with the bed size (handles "King / Medium" etc.)
        const matches = btn.dataset.size.toLowerCase().startsWith(needle);
        btn.style.display = matches ? '' : 'none';
        if (matches) hasMatch = true;
      });
      // Hide the whole card when it offers nothing in the selected size
      card.style.display = hasMatch ? '' : 'none';
    });
  }

  function resetMattressSelection() {
    window.__mattressVariantId = null;
    clearMattressLabel();
    document.querySelectorAll('.mattress-card__variant-btn').forEach((btn) => {
      btn.classList.remove('mattress-card__variant-btn--selected');
    });
  }

  // After a successful cart add the theme fires cartUpdate via pubsub.
  // Use that to clear the mattress selection so the next Add to Basket
  // starts clean without injecting a stale variant.
  function attachCartResetListener() {
    if (typeof FoxTheme === 'undefined' || !FoxTheme?.pubsub?.subscribe) return;
    FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
      if (!window.__mattressVariantId) return;
      window.__mattressVariantId = null;
      clearMattressLabel();
    });
  }

  if (typeof FoxTheme !== 'undefined') {
    attachCartResetListener();
  } else {
    document.addEventListener('DOMContentLoaded', attachCartResetListener);
  }

  // Filter the mattress list to the selected bed size when the drawer opens.
  // The trigger opens it by toggling the `open` attribute rather than firing
  // the theme's show event, so observe that attribute instead.
  const mattressDrawer = document.getElementById('MattressDrawer');
  if (mattressDrawer) {
    new MutationObserver(() => {
      if (mattressDrawer.hasAttribute('open')) filterMattressVariantsBySize();
    }).observe(mattressDrawer, { attributes: true, attributeFilter: ['open'] });
  }

  // When bed size changes: reset selection and re-filter.
  // Delegate from document: a variant change re-renders <variant-selects>
  // via the Section Rendering API, replacing the node, so a listener bound
  // to the element only survives the first change.
  document.addEventListener('change', (event) => {
    const changedInput = event.target;
    if (!(changedInput instanceof HTMLInputElement)) return;
    const variantSelects = changedInput.closest('variant-selects');
    if (!variantSelects) return;
    // Size is always the first fieldset (option index 0)
    const sizeFieldset = variantSelects.querySelector('fieldset');
    if (!sizeFieldset || !sizeFieldset.contains(changedInput)) return;

    resetMattressSelection();
    if (document.getElementById('MattressDrawer')?.hasAttribute('open')) {
      filterMattressVariantsBySize();
    }
  });

  /* ── Row click: toggle variant selector ────────────────────────────── */
  document.addEventListener('click', (event) => {
    const row = event.target.closest('.mattress-card__row');
    if (row) {
      event.stopPropagation();
      const card = row.closest('.mattress-card');
      const selector = card.querySelector('.mattress-card__variants');
      const bagBtn = card.querySelector('.mattress-card__bag-btn');
      const isOpen = !selector.hidden;

      document.querySelectorAll('.mattress-card__variants:not([hidden])').forEach((el) => {
        el.hidden = true;
        el.setAttribute('aria-hidden', 'true');
        el.closest('.mattress-card')
          ?.querySelector('.mattress-card__bag-btn')
          ?.setAttribute('aria-expanded', 'false');
      });

      selector.hidden = isOpen;
      selector.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
      bagBtn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      return;
    }

    /* ── Variant selection ──────────────────────────────────────────── */
    const variantBtn = event.target.closest('.mattress-card__variant-btn');
    if (variantBtn) {
      selectMattressVariant(variantBtn);
    }
  });

  function selectMattressVariant(variantBtn) {
    const { variantId, variantTitle, productTitle } = variantBtn.dataset;

    selectedMattressVariantId = variantId;
    window.__mattressVariantId = variantId; // consumed by theme.liquid fetch patch

    const label =
      variantTitle && variantTitle !== 'Default Title'
        ? `${productTitle} / ${variantTitle}`
        : productTitle;

    const valueEl = document.getElementById('MattressValue');
    if (valueEl) valueEl.textContent = label;

    document.querySelectorAll('.mattress-card__variants').forEach((el) => {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('.mattress-card__bag-btn').forEach((btn) =>
      btn.setAttribute('aria-expanded', 'false')
    );

    const drawer = document.getElementById('MattressDrawer');
    if (drawer) drawer.removeAttribute('open');
  }
})();
